import React, { useEffect, useState } from "react";
import { getRecordsService } from "../../services/commonservices";
import withAuthorization from "../common/auth/withAuthorization";
import { PATIENTS_PAGE_SIZE, ROLES_ALL, ROUTES_HOME, ROUTES_PATIENT } from "../../utils/constants";
import { DataGrid } from "@material-ui/data-grid";
import PaginationHandlerComponent from "../common/PaginationHandlerComponent";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { makeStyles, Typography } from "@material-ui/core";
import BackButtonComponent from "../common/BackButtonComponent";
import { changeTotalPatientsAction } from "../../reducers/searchOmimState";
import { useNavigate } from "react-router";
import { storeSearchPatientIdActionCreator } from "../../reducers/patientState";
import { differenceInYears, format, parse } from "date-fns";
import { originPatientListActionCreator } from "../../reducers/commonState";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(6),
    display: "flex",
    flexDirection: "column",
    gap: "2rem",
    padding: "1rem",
    maxHeight: "calc(100vh - 160px)",
    overflow: "scroll",
    [theme.breakpoints.down("sm")]: {
      padding: "1rem",
      gap: "1rem",
    },
  },
  header: {
    display: "flex",
    justifyContent: "flex-start",
  },
  dataGridContainer: {
    height: 400,
    width: "100%",
  },
  panelPaper: {
    display: "grid",
    gap: "1rem",
    borderRadius: 32,
    padding: "2rem",
    boxShadow: "0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)",
    backgroundColor: "white",
    [theme.breakpoints.down("sm")]: {
      boxShadow: "none",
      padding: 0,
    },
  },
  overlayMessage: {
    height: "100%",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
  },
}));

const PatientsComponent = () => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, mobileVersion } = useSelector((state) => state.common.loading);
  const totalPatients = useSelector((state) => state.searchOmim.totalPatients);

  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const lastPage = Math.ceil(totalPatients / PATIENTS_PAGE_SIZE);

  const recordsCallback = (response) => {
    setRecords(response.data);
    dispatch(changeTotalPatientsAction(parseInt(response.headers["pagination-total-results"])));
  };

  const handleGetRecords = (value) => {
    const newPage = !!value ? page + 1 : page - 1;

    setPage(newPage);

    const offset = PATIENTS_PAGE_SIZE * (newPage - 1) || 0;

    getRecordsService(PATIENTS_PAGE_SIZE, offset, recordsCallback);
  };

  useEffect(() => {
    getRecordsService(PATIENTS_PAGE_SIZE, 0, recordsCallback);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoToSeePatient = (value) => {
    localStorage.setItem("genome-patient-id", value.row.patient_id);
    dispatch(storeSearchPatientIdActionCreator(value.row.patient_id));
    dispatch(originPatientListActionCreator());
    localStorage.setItem("genome-new-patient", JSON.stringify(false));
    navigate(ROUTES_PATIENT);
  };

  const formatAge = (timestamp) => {
    const birthday = format(timestamp * 1000, "dd/MM/yyyy");
    const date = parse(birthday, "dd/MM/yyyy", new Date());
    return differenceInYears(new Date(), date);
  };

  const formatDate = (timestamp) => {
    return format(new Date(timestamp * 1000), "kk:mm:ss - dd/MM/yyyy");
  };

  const columns = [
    {
      field: "patient_id",
      headerName: "ID",
      width: mobileVersion ? 80 : null,
      flex: mobileVersion ? null : 0.5,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "birth_date",
      headerName: intl.formatMessage({ id: "age" }),
      width: mobileVersion ? 90 : null,
      flex: mobileVersion ? null : 0.5,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const age = formatAge(params.formattedValue);
        return <Typography>{age}</Typography>;
      },
    },
    {
      field: "diagnosis_status",
      headerName: intl.formatMessage({ id: "diagnosis_status" }),
      width: mobileVersion ? 120 : null,
      flex: mobileVersion ? null : 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography>{intl.formatMessage({ id: "diagnosis_status_" + params.row.diagnosis_status })}</Typography>
      ),
    },
    {
      field: "last_update",
      headerName: intl.formatMessage({ id: "lastUpdate" }),
      width: mobileVersion ? 200 : null,
      flex: mobileVersion ? null : 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const dateTime = formatDate(params.formattedValue);
        return <Typography>{dateTime}</Typography>;
      },
    },
  ];

  return (
    <div className={classes.root}>
      <div className={classes.panelPaper}>
        <div className={classes.header}>
          <BackButtonComponent url={ROUTES_HOME} />
        </div>
        <Typography variant={"h2"}>{intl.formatMessage({ id: "patient_list" })}</Typography>
        <div className={classes.dataGridContainer}>
          <DataGrid
            autoHeight={false}
            rows={records}
            columns={columns}
            onRowClick={handleGoToSeePatient}
            pageSize={PATIENTS_PAGE_SIZE}
            rowsPerPageOptions={[PATIENTS_PAGE_SIZE]}
            disableSelectionOnClick
            rowHeight={60}
            hideFooterPagination
            hideFooter
            loading={loading}
            components={{
              NoRowsOverlay: () => (
                <div className={classes.overlayMessage}>
                  <Typography>{intl.formatMessage({ id: "no_records_found" })}</Typography>
                </div>
              ),
            }}
          />
        </div>
        {!!records?.length && (
          <PaginationHandlerComponent
            handleClick={handleGetRecords}
            disablePrevious={!!(!records?.length || page === 1)}
            disableNext={!!(!records?.length || (!!page && !!lastPage && page === lastPage) || !lastPage)}
            page={page}
            lastPage={lastPage}
          />
        )}
      </div>
    </div>
  );
};

export default withAuthorization(ROLES_ALL)(PatientsComponent);
